import React, { useState } from "react";
import { Box, Divider, Button, CircularProgress, Grid, Typography, Checkbox, FormControlLabel, IconButton, Paper, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Delete, Settings, UploadFile, VisibilityOffRounded, PersonAddAlt } from "@mui/icons-material";
import DynamicForm from "Components/DynamicForm";
import { Dropdown } from "Components/DropDown";
import Service from "./Service";
import "./Formpage.scss";
import Swal from "sweetalert2";
import { postRequest, getRequest } from "Includes/api/apicall";
import axiosAPI from "Includes/api/api";
import { POST_URL, GET_URL } from "Includes/urls";
import AgreementSummary from "Containers/Agreement/AgreementSummary";
import { validateIndianMobileDigits10, validateMobileNumberFormat } from "Includes/functions";
import { VisibilityRounded } from "@mui/icons-material";
import {
  formatOrderFormNumber,
  grandTotalAgreement,
  erpTotalInclGst,
  parseBillingDateOnly,
  otherServiceCalculationExplain,
} from "Containers/Agreement/agreementFinance";
import { useNavigate } from "react-router-dom";

const getTodayISO = () => new Date().toISOString().split("T")[0];
const deepClone = (v) => JSON.parse(JSON.stringify(v));

/** Agreement API: digitalfeature is { [id]: [price, renewal_price] } (legacy) or object rows. */
const parseDigitalFeaturePayload = (raw) => {
  const out = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [id, v] of Object.entries(raw)) {
    if (v == null) continue;
    let price = "";
    let renewal_price = "";
    let billing_from = "";
    let billing_to = "";
    let calculation_type = "monthwise";
    if (Array.isArray(v)) {
      price = v[0] != null && v[0] !== "" ? String(v[0]) : "";
      renewal_price = v[1] != null && v[1] !== "" ? String(v[1]) : "";
    } else if (typeof v === "object") {
      price = v.price != null && v.price !== "" ? String(v.price) : "";
      renewal_price = v.renewal_price != null && v.renewal_price !== "" ? String(v.renewal_price) : "";
      billing_from = v.billing_from != null && v.billing_from !== "" ? String(v.billing_from) : "";
      billing_to = v.billing_to != null && v.billing_to !== "" ? String(v.billing_to) : "";
      calculation_type = v.calculation_type != null && v.calculation_type !== "" ? String(v.calculation_type) : "monthwise";
    }
    out[String(id)] = { price, renewal_price, billing_from, billing_to, calculation_type };
  }
  return out;
};

/** Inclusive month count for digital billing range; null if dates missing or invalid (no default). */
const getDigitalRowMonthCount = (billingFrom, billingTo) => {
  const fromStr = billingFrom != null ? String(billingFrom).trim() : "";
  const toStr = billingTo != null ? String(billingTo).trim() : "";
  if (!fromStr || !toStr) return null;
  const from = parseBillingDateOnly(fromStr);
  const to = parseBillingDateOnly(toStr);
  if (!from || !to || to < from) return null;
  const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
  return months > 0 ? months : null;
};

const getDigitalRowYearCount = (billingFrom, billingTo) => {
  const fromStr = billingFrom != null ? String(billingFrom).trim() : "";
  const toStr = billingTo != null ? String(billingTo).trim() : "";
  if (!fromStr || !toStr) return null;
  const from = parseBillingDateOnly(fromStr);
  const to = parseBillingDateOnly(toStr);
  if (!from || !to || to < from) return null;
  let years = to.getFullYear() - from.getFullYear();
  if (to.getMonth() > from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() > from.getDate())) {
    years += 1;
  }
  return years > 0 ? years : 1;
};

const getDigitalFeatureQuantity = (calculationType, row, formData) => {
  if (calculationType === "yearwise") return getDigitalRowYearCount(row?.billing_from, row?.billing_to) || 1;
  if (calculationType === "studentcount") return Number(formData?.numberOfStudents || 0) || 0;
  return getDigitalRowMonthCount(row?.billing_from, row?.billing_to);
};

const buildDigitalFeatureForApi = (selection, isEditMode, formDataForQuantity = {}) => {
  const payload = {};
  for (const [id, row] of Object.entries(selection || {})) {
    const unit = row.price === "" || row.price == null ? null : Number(row.price);
    const p = unit != null && !Number.isNaN(unit) ? Number(unit.toFixed(2)) : null;
    const r =
      row.renewal_price === "" || row.renewal_price == null ? null : Number(row.renewal_price);
    const quantity = getDigitalFeatureQuantity(row.calculation_type || "monthwise", row, formDataForQuantity);
    const total =
      !isEditMode && p != null && quantity != null ? Number((p * quantity).toFixed(2)) : null;
    payload[String(id)] = {
      price: p,
      renewal_price: r,
      calculation_type: row.calculation_type || "monthwise",
      billing_from: row.billing_from || null,
      billing_to: row.billing_to || null,
      total_price: total,
      quantity,
    };
  }
  return payload;
};

const initFormData = () => {
  const today = getTodayISO();
  const year = parseInt(today.split("-")[0], 10);

  return {
    company: null,
    schoolName: "",
    schoolAddress: "",
    numberOfStudents: "",
    contactPerson: "",
    contactPersonTitle: "",
    contactPersonEmail: "",
    contactPersonPhoneno: "",
    agreementdate: today,

    sales_person: null,

    increasePercentage: "5",
    subscription: [],
    addOns: [],
    planNotes: [],
    perId: "",
    fromDate: today,
    toDate: `${year + 3}-05-31`,
    paymentTerms: [{ payment: "100", date: today }],
    peramount: "",
    pricing: [],
    smscost: "0.25",
    emailcost: "0.10",
    fsmscost: "5000",
    femailcost: "10000",
    ivrcost: "0.37",
    orderformnumber: formatOrderFormNumber(),
    gst: "18",
    whatsappcost: "",
    amountEntry: "Number of students",
  };
};

const AgreementAdd = (props) => {
  const mode = props?.mode || "agreement"; // 'agreement' | 'quotation'
  const history = useNavigate();

  const [selectedTerm, setSelectedTerm] = React.useState("1");
  const [error, setError] = React.useState("");
  const [submitDisable, setSubmitDisable] = React.useState(false);
  const [textAreas, setTextAreas] = React.useState([1]);
  const [priceAreas, setPriceAreas] = React.useState([1]);
  const [addonAreas, setAddonAreas] = React.useState([1]);
  const [planNotesAreas, setPlanNotesAreas] = React.useState([1]);
  const [salesSelect, setSalesSelect] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [agreementId, setAgreementId] = React.useState(null);

  const baseRef = React.useRef(null);
  if (!baseRef.current) baseRef.current = initFormData();
  const [dynamicFormKey, setDynamicFormKey] = React.useState(0);
  const [initialValues, setInitialValues] = React.useState(() => deepClone(baseRef.current));

  const [usersList, setUsersList] = useState([]);
  const [companiesList, setCompaniesList] = React.useState([]);
  const [selectedErpFeatures, setSelectedErpFeatures] = React.useState([]);
  const [selectedServices, setSelectedServices] = React.useState({});
  const [formData, setFormData] = React.useState(() => deepClone(baseRef.current));
  const [servicePlanList, setServicePlanList] = React.useState([]);
  // Document management state
  const [existingDocuments, setExistingDocuments] = React.useState([]);
  const [documentsToDelete, setDocumentsToDelete] = React.useState([]);
  const [newDocumentsToUpload, setNewDocumentsToUpload] = React.useState([]);
  const documentFileInputRef = React.useRef(null);
  const [ERPFeatureList, setERPFeatureList] = React.useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [DigitalMarketingFeatureList, setDigitalMarketingFeatureList] = React.useState([]);
  const [serviceToggles, setServiceToggles] = React.useState({
    erp: true,
    digital: false,
    idCard: false,
  });
  const [paymentEditMode, setPaymentEditMode] = React.useState("percentage"); // 'percentage' | 'amount'
  /** { [featureId]: { price, renewal_price, billing_from, billing_to } } for selected digital marketing rows */
  const [digitalFeatureSelection, setDigitalFeatureSelection] = React.useState({});
  const [phoneValidationError, setPhoneValidationError] = React.useState("");
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingSubmitData, setPendingSubmitData] = React.useState(null);
  const [salespersonDialogOpen, setSalespersonDialogOpen] = React.useState(false);
  const [salespersonForm, setSalespersonForm] = React.useState({
    username: "",
    mobile_num: "",
    email: "",
    password: "",
  });
  const [salespersonSaving, setSalespersonSaving] = React.useState(false);

  const today = getTodayISO();

  const fetchSalesUsers = React.useCallback(() => {
    return getRequest(GET_URL.user.api, { groups: 3 }, props)
      .then((response) => {
        if (response && response.status === 200) {
          const users = response.data?.data || response.data || [];
          setUsersList(Array.isArray(users) ? users : []);
          return Array.isArray(users) ? users : [];
        }
        return [];
      })
      .catch(() => []);
  }, [props]);

  React.useEffect(() => {
    fetchSalesUsers();
    getServicePlanList();
  }, [fetchSalesUsers]);
  

  // Fetch companies list (only in edit mode) - exclude companies already mapped to other agreements
  React.useEffect(() => {
    if (window.location.pathname.includes("/edit")) {
      const params = new URLSearchParams(window.location.search);
      const agreementId = params.get('id');
      
      // Fetch unmapped companies using the new endpoint
      const url = GET_URL.company.api;
      const params_obj = agreementId ? { unmapped_for_agreement: 1, exclude_agreement_id: agreementId } : { unmapped_for_agreement: 1 };
      
      getRequest(url, params_obj, props)
        .then((response) => {
          if (response && response.status === 200) {
            const companies = response.data?.data || [];
            setCompaniesList(companies);
          }
        })
        .catch(() => {
          // Silently fail
        });
    }
  }, []);

  // Check if we're in edit mode
  React.useEffect(() => {
    if (window.location.pathname.includes("/edit")) {
      const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
      if (id) {
        setIsEditMode(true);
        setAgreementId(id);
        setLoading(true);
        setDataLoaded(false); // Reset data loaded flag
        // Fetch agreement data
        getRequest(`${GET_URL.agreements.api}${id}/`, {}, props)
          .then((response) => {
            if (response && response.status === 200) {
              const agreementData = response.data?.data || response.data;
              
              // Format dates to YYYY-MM-DD format if they come in a different format
              const formatDate = (dateStr) => {
                if (!dateStr) return null;
                // If it's already in YYYY-MM-DD format, return as is
                if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                  return dateStr;
                }
                // Try to parse and format
                try {
                  const date = new Date(dateStr);
                  if (!isNaN(date.getTime())) {
                    return date.toISOString().split('T')[0];
                  }
                } catch (e) {
                  // ignore
                }
                return dateStr;
              };
              
              // Map API response to form data structure
              const mappedData = {
                company: agreementData.company || null,
                schoolName: agreementData.schoolName || "",
                schoolAddress: agreementData.schoolAddress || "",
                numberOfStudents: agreementData.numberOfStudents || "",
                contactPerson: agreementData.contactPerson || "",
                contactPersonTitle: agreementData.contactPersonTitle || "",
                contactPersonEmail: agreementData.contactPersonEmail || "",
                contactPersonPhoneno: agreementData.contactPersonPhoneno || "",
                agreementdate: formatDate(agreementData.agreementdate) || today,
                sales_person: agreementData.sales_person != null ? String(agreementData.sales_person) : "",
                increasePercentage: agreementData.increasePercentage || "",
                subscription: agreementData.subscription || [],
                addOns: agreementData.addOnsList || [],
                planNotes: agreementData.planNotes || [],
                perId: agreementData.perId || "",
                fromDate: formatDate(agreementData.fromDate) || today,
                toDate: formatDate(agreementData.toDate) || `${parseInt(today.split("-")[0], 10) + 3}-05-31`,
                paymentTerms: agreementData.paymentTerms || [],
                peramount: agreementData.peramount || "",
                pricing: agreementData.pricing || [],
                smscost: agreementData.smscost || "",
                emailcost: agreementData.emailcost || "",
                fsmscost: agreementData.fsmscost || "",
                femailcost: agreementData.femailcost || "",
                ivrcost: agreementData.ivrcost || "",
                orderformnumber: agreementData.orderformnumber || "",
                gst: agreementData.gst || "",
                whatsappcost: agreementData.whatsappcost || "",
                amountEntry: agreementData.amountEntry || "",
              };
              
              // Replace initialValues entirely instead of merging
              setInitialValues(mappedData);
              setFormData(mappedData);
              
              // Set text areas, price areas, addon areas
              if (Array.isArray(agreementData.subscription) && agreementData.subscription.length) {
                setTextAreas(agreementData.subscription.map((_, i) => i + 1));
              }
              if (Array.isArray(agreementData.pricing) && agreementData.pricing.length) {
                setPriceAreas(agreementData.pricing.map((_, i) => i + 1));
              }
              if (Array.isArray(agreementData.addOns) && agreementData.addOns.length) {
                setAddonAreas(agreementData.addOns.map((_, i) => i + 1));
              }
              if (Array.isArray(agreementData.planNotes) && agreementData.planNotes.length) {
                setPlanNotesAreas(agreementData.planNotes.map((_, i) => i + 1));
              }
              
              // Set payment terms count
              if (Array.isArray(agreementData.paymentTerms) && agreementData.paymentTerms.length > 0) {
                setSelectedTerm(String(agreementData.paymentTerms.length));
              }
              
              // Set sales person select
              if (agreementData.sales_person) {
                setSalesSelect(String(agreementData.sales_person));
              }
              
              // Set ERP features (ids as strings, or legacy display names) and services
              if (Array.isArray(agreementData.erpfeature)) {
                setSelectedErpFeatures(agreementData.erpfeature.map((x) => String(x)));
              }
              if (agreementData.services && typeof agreementData.services === "object") {
                const svcOnly = {};
                for (const [k, val] of Object.entries(agreementData.services)) {
                  if (val && typeof val === "object" && !Array.isArray(val) && "Amount" in val) {
                    svcOnly[k] = val;
                  }
                }
                setSelectedServices(svcOnly);
              }
              setDigitalFeatureSelection(parseDigitalFeaturePayload(agreementData.digitalfeature));
              setServiceToggles({
                erp: Array.isArray(agreementData.erpfeature) && agreementData.erpfeature.length > 0,
                digital:
                  !!agreementData.digitalfeature &&
                  typeof agreementData.digitalfeature === "object" &&
                  Object.keys(agreementData.digitalfeature).length > 0,
                idCard: !!agreementData.perId && Number(agreementData.perId) > 0,
              });
              
              // Load existing documents
              const documents = Array.isArray(agreementData.agreement_documents) ? agreementData.agreement_documents : [];
              setExistingDocuments(documents);
              setDocumentsToDelete([]);
              setNewDocumentsToUpload([]);
            }
          })
          .catch(() => {
            Swal.fire({
              position: "top-end",
              type: "error",
              title: "Error",
              text: "Failed to load agreement data",
              showConfirmButton: false,
              timer: 1500,
            });
            history("/agreement/list");
          })
          .finally(() => setLoading(false));
      } else {
        // No ID provided, redirect to list
        history("/agreement/list");
      }
    }
  }, [props.location, history, today]);

  // Remount DynamicForm when data is loaded in edit mode
  // We need to remount after initialValues are set so fieldDetails useMemo recalculates
  const [dataLoaded, setDataLoaded] = React.useState(false);
  
  React.useEffect(() => {
    if (isEditMode && !loading && initialValues.orderformnumber && !dataLoaded) {
      // Mark data as loaded and remount DynamicForm to pick up new fieldDetails
      setDataLoaded(true);
      // Use a small delay to ensure useMemo hooks have recalculated
      setTimeout(() => {
        setDynamicFormKey((k) => k + 1);
      }, 0);
    }
  }, [isEditMode, loading, initialValues.orderformnumber, dataLoaded]);

  const updateParent = (name, value) => {
    if (name === "contactPersonPhoneno") {
      const raw = String(value || "").trim();
      const startsWithPlus = raw.startsWith("+");
      const digitsOnly = raw.replace(/\D/g, "").slice(0, 12);
      const normalized = startsWithPlus ? `+${digitsOnly}` : digitsOnly;
      setFormData((prev) => ({ ...prev, [name]: normalized }));
      if (!normalized) {
        setPhoneValidationError("");
      } else {
        const check = validateMobileNumberFormat(normalized, false);
        setPhoneValidationError(check?.isValid ? "" : (check?.error || "Invalid phone number"));
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "sales_person") {
      if (value) setSalesSelect(value);
      else setSalesSelect("");
    }
  };

  const inferServiceTogglesFromPlan = React.useCallback((plan) => {
    if (!plan) return null;
    const serviceName = String(plan.service_name || plan.service?.name || "").toLowerCase();
    const hasErp = Array.isArray(plan.erp_features) && plan.erp_features.length > 0;
    const hasDigital =
      Array.isArray(plan.digital_marketing_features) && plan.digital_marketing_features.length > 0;
    const mentionsErp = serviceName.includes("erp");
    const mentionsDigital = serviceName.includes("digital");
    const mentionsIdCard = serviceName.includes("id card") || serviceName.includes("idcard");
    const hasAnyHint = hasErp || hasDigital || mentionsErp || mentionsDigital || mentionsIdCard;
    if (!hasAnyHint) return null;
    return {
      erp: hasErp || mentionsErp,
      digital: hasDigital || mentionsDigital,
      idCard: mentionsIdCard,
    };
  }, []);

  const handleServiceToggleChange = (key) => (event) => {
    const checked = event.target.checked;
    setServiceToggles((prev) => ({ ...prev, [key]: checked }));
    if (!checked) {
      if (key === "erp") setSelectedErpFeatures([]);
      if (key === "digital") setDigitalFeatureSelection({});
      if (key === "idCard") updateParent("perId", "");
    }
  };
  const getServicePlanList = () => {
    const url = GET_URL.plans.api;
    getRequest(url, {}, props).then(response => {
      if (response && response.status === 200) {
        setServicePlanList(response.data.data || []);
      }
    }).catch(() => {
      console.log('error',error)
    });
  };
  /** Equal integer split of 100% (e.g. 2 → 50,50; 3 → 34,33,33). */
  const splitPaymentPercentsEqual = (n) => {
    if (!n || n < 1) return [];
    const base = Math.floor(100 / n);
    let rem = 100 - base * n;
    return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
  };

  const handleTermChange = (e) => {
    const term = e.target.value;
    setSelectedTerm(term);
    const numberOfInputs = term ? parseInt(term, 10) : 0;
    const percents = splitPaymentPercentsEqual(numberOfInputs);
    const y = parseInt(today.split("-")[0], 10);
    setFormData((prev) => {
      const paymentTerms = Array.from({ length: numberOfInputs }, (_, index) => {
        const defaultDate =
          index === 0
            ? today
            : index === 1
              ? `${y}-10-01`
              : index === 2
                ? `${y + 1}-01-01`
                : index === 3
                  ? `${y + 1}-04-01`
                  : "";
        return {
          payment: percents[index] != null ? String(percents[index]) : "",
          date: defaultDate,
        };
      });
      return { ...prev, paymentTerms };
    });
    setError("");
  };

  const handlePaymentChange = (index, field, value) => {
    setFormData((prev) => {
      const paymentTerms = [...(prev.paymentTerms || [])];
      const currentTerm = { ...(paymentTerms[index] || {}) };
      const grandTotal = Number(erpTotalInclGst(prev)) || 0;

      const othersPct = paymentTerms.reduce(
        (sum, t, i) => (i === index ? sum : sum + (parseFloat(t?.payment) || 0)),
        0
      );
      const maxPctForRow = Math.max(0, 100 - othersPct);

      if (field === "amount") {
        const raw = String(value).trim();
        // In Amount mode, store amounts as the source of truth.
        currentTerm.amount = value;

        if (paymentEditMode === "amount") {
          // allow typing, and clamp to remaining of grand total (if known)
          if (raw === "" || raw === ".") {
            // keep as-is
          } else {
            let amountNum = parseFloat(value);
            if (Number.isFinite(amountNum) && grandTotal > 0) {
              const otherAmtSum = paymentTerms.reduce((sum, t, i) => {
                if (i === index) return sum;
                return sum + (parseFloat(t?.amount) || 0);
              }, 0);
              const remainingAmt = Math.max(0, grandTotal - otherAmtSum);
              amountNum = Math.min(Math.max(0, amountNum), remainingAmt);
              currentTerm.amount = String(amountNum);
            }
          }
          // Do not calculate % here; it will be derived on submit.
        } else {
          // Percentage mode: if amount is edited (should be rare), convert amount -> %
          if (raw === "" || raw === ".") {
            currentTerm.payment = "";
          } else {
            let amountNum = parseFloat(value);
            if (!Number.isFinite(amountNum)) {
              currentTerm.payment = "";
            } else if (grandTotal > 0) {
              const maxAmt = (grandTotal * maxPctForRow) / 100;
              amountNum = Math.min(Math.max(0, amountNum), maxAmt);
              currentTerm.amount = String(amountNum);
              const pct = (amountNum / grandTotal) * 100;
              currentTerm.payment = pct.toFixed(2);
            }
          }
        }
      } else if (field === "payment") {
        const pctNum = parseFloat(value);
      
        currentTerm.amount = "";
        if (!Number.isFinite(pctNum)) {
          currentTerm.payment = value;
        } else {
          // When editing by %, allow any % up to 100 (others will auto-adjust).
          const clamped = Math.min(Math.max(0, pctNum), 100);
          currentTerm.payment = clamped;
          
          // 🔥 Auto-adjust remaining rows
          const remaining = 100 - clamped;
          const otherIndexes = paymentTerms.map((_, i) => i).filter(i => i !== index);
          
          if (otherIndexes.length > 0) {
            const split = remaining / otherIndexes.length;
      
            otherIndexes.forEach(i => {
              paymentTerms[i] = {
                ...paymentTerms[i],
                payment: split.toFixed(2),
              };
            });
          }
        }
      
      } else {
        currentTerm[field] = value;
      }

      paymentTerms[index] = currentTerm;

      if (paymentEditMode === "amount") {
        const amtSum = paymentTerms.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0);
        if (grandTotal > 0) {
          const diff = Math.abs(amtSum - grandTotal);
          if (diff > 0.01) setError(`Total instalment amount must equal ₹ ${grandTotal.toFixed(2)}`);
          else setError("");
        } else {
          // Can't validate against total until other sections are filled.
          setError("");
        }
      } else {
        const totalPayment = paymentTerms.reduce((sum, term) => sum + (parseFloat(term.payment) || 0), 0);
        if (totalPayment > 100) setError("Total payment percentage exceeds 100%");
        else if (paymentTerms.length > 0 && totalPayment < 100) setError("Total payment percentage must be exactly 100%");
        else setError("");
      }

      // auto-fill a few suggested term dates
      const y = parseInt(today.split("-")[0], 10);
      // Term 1 date defaults to today's date
      if (paymentTerms[0] && !paymentTerms[0].date) {
        paymentTerms[0].date = today;
      }
      if (paymentTerms[1] && !paymentTerms[1].date) {
        paymentTerms[1].date = `${y}-10-01`;
      }
      if (paymentTerms[2] && !paymentTerms[2].date) {
        paymentTerms[2].date = `${y + 1}-01-01`;
      }
      if (paymentTerms[3] && !paymentTerms[3].date) {
        paymentTerms[3].date = `${y + 1}-04-01`;
      }

      return { ...prev, paymentTerms };
    });
  };

  const addTextArea = () => setTextAreas((prev) => [...prev, prev.length + 1]);
  const deleteTextArea = () => {
    setTextAreas((prev) => prev.slice(0, -1));
    setFormData((prev) => ({ ...prev, subscription: (prev.subscription || []).slice(0, -1) }));
  };

  const addPriceArea = () => setPriceAreas((prev) => [...prev, Date.now()]);
  const deletePriceArea = () => {
    setPriceAreas((prev) => prev.slice(0, -1));
    setFormData((prev) => ({ ...prev, pricing: (prev.pricing || []).slice(0, -1) }));
  };

  const addAddonArea = () => setAddonAreas((prev) => [...prev, Date.now()]);
  const deleteAddonArea = () => {
    setAddonAreas((prev) => prev.slice(0, -1));
    setFormData((prev) => ({ ...prev, addOns: (prev.addOns || []).slice(0, -1) }));
  };

  const addPlanNotesArea = () => setPlanNotesAreas((prev) => [...prev, prev.length + 1]);
  const deletePlanNotesArea = () => {
    setPlanNotesAreas((prev) => prev.slice(0, -1));
    setFormData((prev) => ({ ...prev, planNotes: (prev.planNotes || []).slice(0, -1) }));
  };

  const validate = (dataToSubmit) => {
    if (error) return "Fix the errors before submitting.";
    if (!dataToSubmit.schoolName) return "Enter Institute Name";
    if (!dataToSubmit.schoolAddress) return "Enter Institute Address";
    if (!dataToSubmit.contactPerson) return "Enter Contact Person";
    if (!dataToSubmit.contactPersonTitle) return "Enter Contact Person Title";
    const phoneValidation = validateMobileNumberFormat(dataToSubmit.contactPersonPhoneno, true);
    if (!phoneValidation.isValid) {
      return phoneValidation.error || "Enter Contact Person Phone No";
    }
    
    // Date validations: agreement_date, from_date, to_date should be between 2020-01-01 and 2050-12-31
    const minDate = "2020-01-01";
    const maxDate = "2050-12-31";
    
    if (!dataToSubmit.agreementdate) return "Enter Agreement Date";
    if (dataToSubmit.agreementdate < minDate || dataToSubmit.agreementdate > maxDate) {
      return "Agreement Date must be between 2020-01-01 and 2050-12-31";
    }
    
    if (!dataToSubmit.fromDate) return "Enter From Date";
    if (dataToSubmit.fromDate < minDate || dataToSubmit.fromDate > maxDate) {
      return "From Date must be between 2020-01-01 and 2050-12-31";
    }
    
    if (!dataToSubmit.toDate) return "Enter To Date";
    if (dataToSubmit.toDate < minDate || dataToSubmit.toDate > maxDate) {
      return "To Date must be between 2020-01-01 and 2050-12-31";
    }
    
    // to_date should be greater than from_date and agreement_date
    if (dataToSubmit.toDate <= dataToSubmit.fromDate) {
      return "To Date must be greater than From Date";
    }
    if (dataToSubmit.toDate <= dataToSubmit.agreementdate) {
      return "To Date must be greater than Agreement Date";
    }

    if (dataToSubmit.amountEntry === "Number of students") {
      if (!dataToSubmit.numberOfStudents) return "Enter Number of students";
      const numStudents = Number(dataToSubmit.numberOfStudents);
      if (numStudents < 1 || numStudents > 1000000) {
        return "Number of students must be between 1 and 1000000";
      }
      if (!dataToSubmit.peramount) return "Enter Amount per head";
      const perAmount = Number(dataToSubmit.peramount);
      // if (perAmount < 1 || perAmount > 10000) {
      //   return "Amount per student must be between 1 and 10000";
      // }
    } else if (dataToSubmit.amountEntry === "Up to student") {
      if (!dataToSubmit.numberOfStudents) return "Enter Up to Students";
      const numStudents = Number(dataToSubmit.numberOfStudents);
      if (numStudents < 1 || numStudents > 1000000) {
        return "Number of students must be between 1 and 1000000";
      }
      if (!dataToSubmit.peramount) return "Enter Amount";
      const perAmount = Number(dataToSubmit.peramount);
      // if (perAmount < 1 || perAmount > 10000) {
      //   return "Amount per student must be between 1 and 10000";
      // }
    } else {
      return "Select Up to student / Number of student";
    }

    if (dataToSubmit.gst === null || dataToSubmit.gst === undefined || dataToSubmit.gst === "") return "Enter GST in %";
    if (isNaN(dataToSubmit.gst)) return "GST should be a Number";
    if (dataToSubmit.fsmscost === null || dataToSubmit.fsmscost === undefined || dataToSubmit.fsmscost === "") return "Enter Free SMS";
    if (Number(dataToSubmit.fsmscost) > 20000) return "Free sms should not be more than 20000";
    if (dataToSubmit.femailcost === null || dataToSubmit.femailcost === undefined || dataToSubmit.femailcost === "") return "Enter Free Email";
    if (dataToSubmit.smscost === null || dataToSubmit.smscost === undefined || dataToSubmit.smscost === "") return "Enter Additional SMS Cost";
    if (Number(dataToSubmit.smscost) < 0.15) return "Sms cost should be greater than 0.15";
    if (dataToSubmit.emailcost === null || dataToSubmit.emailcost === undefined || dataToSubmit.emailcost === "") return "Enter Additional Email Cost";
    if (!dataToSubmit.sales_person) return "Select Sales Person";

    if (!isEditMode && digitalFeatureSelection && Object.keys(digitalFeatureSelection).length) {
      for (const fid of Object.keys(digitalFeatureSelection)) {
        const row = digitalFeatureSelection[fid];
        const meta = (DigitalMarketingFeatureList || []).find((f) => String(f.id) === String(fid));
        const label = meta?.name || "Other service feature";
        if (!row?.billing_from || !String(row.billing_from).trim()) {
          return `${label}: enter billing From date`;
        }
        if (!row?.billing_to || !String(row.billing_to).trim()) {
          return `${label}: enter billing To date`;
        }
        const months = getDigitalRowMonthCount(row.billing_from, row.billing_to);
        if (months == null) {
          return `${label}: billing To date must be on or after From date`;
        }
      }
    }

    if (dataToSubmit.perId && Number(dataToSubmit.perId) < 35) return "Amount per id should be greater than 35";
    if (!dataToSubmit.paymentTerms?.[0]) return "Add terms";
    if (dataToSubmit.whatsappcost && Number(dataToSubmit.whatsappcost) < 0.2) return "Whatsapp cost should be greater than 0.20";
    if (dataToSubmit.ivrcost && Number(dataToSubmit.ivrcost) < 0.32) return "IVR cost should be greater than 0.32";

    // Validate Services: Amount and GST (submitted as array; legacy object still supported)
    const serviceEntries = [];
    if (Array.isArray(dataToSubmit.services)) {
      for (const s of dataToSubmit.services) {
        if (!s || !s.name) continue;
        serviceEntries.push([
          s.name,
          { Amount: s.amount, GST: s.gst_percentage, Description: s.description },
        ]);
      }
    } else if (dataToSubmit.services && typeof dataToSubmit.services === "object") {
      serviceEntries.push(...Object.entries(dataToSubmit.services));
    }
    for (const [serviceName, service] of serviceEntries) {
      if (!service) continue;
      if (service.Amount !== undefined && service.Amount !== null && service.Amount !== "") {
        const amount = Number(service.Amount);
        if (isNaN(amount) || amount < 1 || amount > 1000000) {
          return `${serviceName}: Amount must be between 1 and 1000000`;
        }
      }
      if (service.GST !== undefined && service.GST !== null && service.GST !== "") {
        const gst = Number(service.GST);
        if (isNaN(gst) || gst < 0 || gst > 100) {
          return `${serviceName}: GST must be between 0 and 100`;
        }
      }
    }

    return "";
  };

  const getDigitalMarketingFeatureList = () => {
    const url = GET_URL.digitalmarketingfeature?.api;
    getRequest(url, {}, props).then(response => {
      setDigitalMarketingFeatureList(response.data.data || []);
    });
  };
  const getERPFeatureList = () => {
    const url = GET_URL.erpfeature?.api;
    getRequest(url, {}, props).then(response => {
      setERPFeatureList(response.data.data || []);
    });
  };

  React.useEffect(() => {
    getDigitalMarketingFeatureList();
    getERPFeatureList();
  }, []);
  const getSummaryData = () => {
    const selectedSales = (usersList || []).find(
      (u) => String(u.id) === String(formData.sales_person || "")
    );
    return {
      ...formData,
      erpfeature: selectedErpFeatures,
      digitalfeature: buildDigitalFeatureForApi(digitalFeatureSelection, isEditMode, formData),
      services: selectedServices,
      payment_terms_edit_by: paymentEditMode,
      sales_person: formData.sales_person,
      salesPersonName: selectedSales?.username || "",
      salesPersonEmail: selectedSales?.email || "",
      salesPersonPhoneno: selectedSales?.mobile_num || "",
    };
  };

  /** Same totals as summary/PDF, built from any form snapshot (fixes payment-term amount ↔ % sync). */
  const grandTotalFromFormState = React.useCallback(
    (fd) => {
      const selectedSales = (usersList || []).find(
        (u) => String(u.id) === String(fd.sales_person || "")
      );
      const payload = {
        ...fd,
        erpfeature: selectedErpFeatures,
        digitalfeature: buildDigitalFeatureForApi(digitalFeatureSelection, isEditMode, fd),
        services: selectedServices,
        sales_person: fd.sales_person,
        salesPersonName: selectedSales?.username || "",
        salesPersonEmail: selectedSales?.email || "",
        salesPersonPhoneno: selectedSales?.mobile_num || "",
      };
      return grandTotalAgreement(payload);
    },
    [usersList, selectedErpFeatures, digitalFeatureSelection, selectedServices, isEditMode]
  );

  /** Payment terms total basis: ERP only (incl. GST). */
  const paymentScheduleGrandTotal = React.useMemo(
    () => erpTotalInclGst(formData),
    [formData]
  );

  const handleDocumentFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setNewDocumentsToUpload((prev) => [...prev, ...files]);
    }
    // Reset input
    if (e.target) e.target.value = "";
  };

  const handleRemoveNewDocument = (index) => {
    setNewDocumentsToUpload((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleDeleteDocument = (doc) => {
    // Use agreement_document_id if available, otherwise fallback to doc.id
    const docId = doc.agreement_document_id || doc.id;
    setDocumentsToDelete((prev) => {
      if (prev.includes(docId)) {
        return prev.filter((id) => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const buildSubmitData = () => {
    const submittableServices = [];
    for (const [key, val] of Object.entries(selectedServices || {})) {
      if (!val || typeof val !== "object" || Array.isArray(val)) continue;
      if (!("Amount" in val)) continue;
      submittableServices.push({
        name: key,
        amount: val.Amount,
        gst_percentage: val.GST,
        description: val.Description,
      });
    }
    const dataToSubmit = {
      ...formData,
      pricing: formData.pricing.filter((x)=> !!x.trim()),
      subscription: formData.subscription.filter((x)=> !!x.trim()),
      planNotes: (formData.planNotes || []).filter((x)=> !!x.trim()),
      erpfeature: serviceToggles.erp ? selectedErpFeatures : [],
      digitalfeature: serviceToggles.digital ? buildDigitalFeatureForApi(digitalFeatureSelection, isEditMode, formData) : {},
      services: submittableServices,
      sales_person: formData.sales_person,
      perId: serviceToggles.idCard ? formData.perId : "",
      // Ensure company is null for new agreements (not in edit mode)
      company: isEditMode ? formData.company : null,
    };
    // If payment terms were edited by amount, derive percentages right before submit (ERP-only basis)
    if (paymentEditMode === "amount") {
      const gt = Number(erpTotalInclGst(formData)) || 0;
      if (gt > 0 && Array.isArray(dataToSubmit.paymentTerms)) {
        dataToSubmit.paymentTerms = dataToSubmit.paymentTerms.map((t) => {
          const amt = parseFloat(t?.amount) || 0;
          const pct = (amt / gt) * 100;
          return { ...t, payment: Number.isFinite(pct) ? pct.toFixed(2) : "0.00" };
        });
      }
    }
    return dataToSubmit;
  };

  const performSubmit = (dataToSubmit) => {
    setSubmitDisable(true);
    if (isEditMode && agreementId) {
      // Prepare FormData for POST request (to include files)
      const formData = new FormData();
      
      // Append ID to indicate this is an update
      formData.append("id", agreementId.toString());
      
      // Append all form fields
      Object.keys(dataToSubmit).forEach((key) => {
        const value = dataToSubmit[key];
        if (value !== null && value !== undefined) {
          if (typeof value === 'object' && !(value instanceof File)) {
            // Stringify objects/arrays
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      });
      
      // Append document operations
      // documents_to_delete: append each ID separately so backend receives as array
      if (documentsToDelete.length > 0) {
        documentsToDelete.forEach((docId) => {
          formData.append("documents_to_delete", docId.toString());
        });
      }
      
      // new_documents: append each file separately
      if (newDocumentsToUpload.length > 0) {
        newDocumentsToUpload.forEach((file) => {
          formData.append("new_documents", file);
        });
      }
      
      // Update existing agreement using POST (with id in payload)
      postRequest(POST_URL.agreements.api, formData, { ...props, contentType: 'multipart/form-data' })
        .then((response) => {
          if (response && (response.status === 200 || response.status === 201)) {
            Swal.fire({
              position: "top-end",
              icon: "success",
              title: "Success",
              text: "Agreement updated successfully",
              showConfirmButton: false,
              timer: 1500,
            });
            setTimeout(() => history("/agreement/list"), 1500);
            return;
          }
          history('/agreement/list');
        })
        .catch(() => {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Error",
            text: "Failed to update agreement",
            showConfirmButton: false,
            timer: 1500,
          });
        })
        .finally(() => setSubmitDisable(false));
    } else {
      // Create new agreement
      postRequest(POST_URL.agreements.api, dataToSubmit, props)
        .then((response) => {
          if (response && response.status >= 200 && response.status < 300) {
            Swal.fire({
              icon: "success",
              title: "Successfully created",
              text: "The agreement has been saved.",
              showConfirmButton: false,
              timer: 1200,
            });
            setTimeout(() => history("/agreement/list"), 300);
            return;
          }
          history("/agreement/list");
        })
        .catch(() => {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Error",
            text: "Failed to create agreement",
            showConfirmButton: false,
            timer: 1500,
          });
        })
        .finally(() => setSubmitDisable(false));
    }
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const dataToSubmit = buildSubmitData();

    const validationError = validate(dataToSubmit);
    if (validationError) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: validationError,
      });
      return;
    }
    setPendingSubmitData(dataToSubmit);
    setConfirmOpen(true);
  };

  const handleGenerateQuotation = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const dataToSubmit = buildSubmitData();

    const validationError = validate(dataToSubmit);
    if (validationError) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: validationError,
      });
      return;
    }
    history("/quotation/preview", { state: { data: dataToSubmit, autoPrint: true } });
  };

  const handlePlanChange = (planId) => {
    const normalizedPlanId = planId ? String(planId) : "";
    setSelectedPlanId(normalizedPlanId);

    const selectedPlan = servicePlanList.find((p) => String(p.id) === normalizedPlanId);
    if (!selectedPlan) return;
    const inferredToggles = inferServiceTogglesFromPlan(selectedPlan);
    if (inferredToggles) setServiceToggles(inferredToggles);
  
    // 1. Autofill pricing/basic fields
    setFormData((prev) => ({
      ...prev,
      peramount: selectedPlan.base_price || "",
      // Per requirement: always override GST from selected plan GST.
      gst: selectedPlan.default_tax_amount != null ? String(selectedPlan.default_tax_amount) : "",
    }));
    setInitialValues((prev) => ({
      ...prev,
      peramount: selectedPlan.base_price || "",
      gst: selectedPlan.default_tax_amount != null ? String(selectedPlan.default_tax_amount) : "",
    }));
    setDynamicFormKey((k) => k + 1);
  
    // 2. Autofill ERP Features (plan may contain ids or names)
    if (Array.isArray(selectedPlan.erp_features) && (inferredToggles?.erp ?? serviceToggles.erp)) {
      const matchedERP = ERPFeatureList
        .filter((f) =>
          selectedPlan.erp_features.some(
            (item) => String(item) === String(f.id) || String(item) === String(f.name)
          )
        )
        .map((f) => String(f.id));

      setSelectedErpFeatures(matchedERP);
    }

    if (
      Array.isArray(selectedPlan.digital_marketing_features) &&
      (inferredToggles?.digital ?? serviceToggles.digital)
    ) {
      const nextDigital = {};
      DigitalMarketingFeatureList.forEach((f) => {
        if (
          selectedPlan.digital_marketing_features.some(
            (item) => {
              const itemId = item && typeof item === "object" ? item.id : item;
              const itemName = item && typeof item === "object" ? item.name : item;
              return String(itemId) === String(f.id) || String(itemName) === String(f.name);
            }
          )
        ) {
          nextDigital[String(f.id)] = {
            price: f.price != null ? String(f.price) : "",
            renewal_price: f.renewal_price != null ? String(f.renewal_price) : "",
            calculation_type: f.calculation_type || "monthwise",
          };
        }
      });
      setDigitalFeatureSelection(nextDigital);
    } else if (inferredToggles && !inferredToggles.digital) {
      setDigitalFeatureSelection({});
    }
  
    // 4. Autofill description
    if (selectedPlan.description) {
      setFormData((prev) => ({
        ...prev,
        subscription: [selectedPlan.description]
      }));
    }
  };

  const handleSaveNewSalesperson = async () => {
    const u = (salespersonForm.username || "").trim();
    const m = (salespersonForm.mobile_num || "").trim();
    const pw = salespersonForm.password || "";
    if (!u) {
      Swal.fire("Missing name", "Enter a username / display name for the salesperson.", "warning");
      return;
    }
    if (!m) {
      Swal.fire("Missing mobile", "Mobile number is required to create a user.", "warning");
      return;
    }
    const phoneCheck = validateIndianMobileDigits10(m, true);
    if (!phoneCheck?.isValid) {
      Swal.fire("Invalid mobile", phoneCheck?.error || "Enter a valid 10-digit mobile number.", "warning");
      return;
    }
    if (!pw || pw.length < 6) {
      Swal.fire("Password", "Enter a password with at least 6 characters.", "warning");
      return;
    }
    const payload = {
      username: u,
      password: pw,
      mobile_num: m,
      is_staff: false,
      groups: [3],
    };
    const em = (salespersonForm.email || "").trim();
    if (em) payload.email = em;

    setSalespersonSaving(true);
    try {
      const res = await axiosAPI.post(GET_URL.user.api, payload);
      if (!res || res.status < 200 || res.status >= 300) {
        throw new Error("Unexpected response");
      }
      const users = await fetchSalesUsers();
      const created = (users || []).find((x) => String(x.username) === u);
      if (created?.id) {
        updateParent("sales_person", String(created.id));
      }
      setSalespersonDialogOpen(false);
      setSalespersonForm({ username: "", mobile_num: "", email: "", password: "" });
      Swal.fire({ icon: "success", title: "Salesperson added", text: "They are now available in the list.", timer: 1800, showConfirmButton: false });
    } catch (err) {
      const msg =
        err?.response?.data?.mobile_num?.[0] ||
        err?.response?.data?.username?.[0] ||
        err?.response?.data?.groups?.[0] ||
        err?.response?.data?.detail ||
        err?.message ||
        "Could not create user. Check permissions and fields.";
      Swal.fire("Unable to add salesperson", String(msg), "error");
    } finally {
      setSalespersonSaving(false);
    }
  };

  // Re-apply selected plan after async lists load so edit/create prefill stays correct.
  React.useEffect(() => {
    if (!selectedPlanId) return;
    if (!servicePlanList.length) return;
    handlePlanChange(selectedPlanId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ERPFeatureList, DigitalMarketingFeatureList, servicePlanList.length]);

  const salesPeopleList = React.useMemo(() => {
    // Convert users list to dropdown format: [{ id: id, name: username }]
    return (usersList || []).map((user) => ({
      id: user.id,
      name: user.username || user.email || `User ${user.id}`,
    }));
  }, [usersList]);

  const SectionHeading = ({ children, sx: sxProp = {} }) => (
    <Typography
      component="h2"
      variant="subtitle1"
      sx={{
        color: "#0a0a0a",
        fontWeight: 700,
        mb: 1.5,
        mt: 0.5,
        pb: 0.75,
        borderBottom: "1px solid",
        borderColor: "divider",
        ...sxProp,
      }}
    >
      {children}
    </Typography>
  );

  const schoolFieldDetails = React.useMemo(
    () => [
      {
        label: "Institute Name",
        name: "schoolName",
        value: "schoolName",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-school-name",
        default: initialValues.schoolName || "",
        rows: null,
        type: "text",
        maxLength: 100,
      },
      {
        label: "Institute Address",
        name: "schoolAddress",
        value: "schoolAddress",
        md: 12,
        required: true,
        id: "agreement-school-address",
        default: initialValues.schoolAddress || "",
        rows: 4,
        type: "text_area",
        maxLength: 500,
      },
    ],
    [initialValues.schoolAddress, initialValues.schoolName]
  );

  const contactFieldDetails = React.useMemo(
    () => [
      {
        label: "Contact Person Name",
        name: "contactPerson",
        value: "contactPerson",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-contact-person",
        default: initialValues.contactPerson || "",
        rows: null,
        type: "text",
        maxLength: 100,
      },
      {
        label: "Contact Person Title",
        name: "contactPersonTitle",
        value: "contactPersonTitle",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-contact-title",
        default: initialValues.contactPersonTitle || "",
        rows: null,
        type: "text",
        maxLength: 100,
      },
      {
        label: "Contact Person Email",
        name: "contactPersonEmail",
        value: "contactPersonEmail",
        md: 6,
        className: "w-95-per",
        required: false,
        id: "agreement-contact-email",
        default: initialValues.contactPersonEmail || "",
        rows: null,
        type: "text",
        maxLength: 150,
      },
      {
        label: "Contact Person Phone no",
        name: "contactPersonPhoneno",
        value: "contactPersonPhoneno",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-contact-phone",
        default: initialValues.contactPersonPhoneno || "",
        rows: null,
        type: "phone_number",
      },
    ],
    [
      initialValues.contactPerson,
      initialValues.contactPersonEmail,
      initialValues.contactPersonPhoneno,
      initialValues.contactPersonTitle,
    ]
  );

  const agreementBasicsFieldDetails = React.useMemo(
    () => [
      {
        label: "Agreement Date",
        name: "agreementdate",
        value: "agreementdate",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-date",
        default: initialValues.agreementdate || today,
        type: "date",
        minDate: "2020-01-01",
        maxDate: "2050-12-31",
      },
      {
        label: "From Date",
        name: "fromDate",
        value: "fromDate",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-from-date",
        default: initialValues.fromDate || today,
        type: "date",
        minDate: "2020-01-01",
        maxDate: "2050-12-31",
      },
      {
        label: "To Date",
        name: "toDate",
        value: "toDate",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-to-date",
        default: initialValues.toDate || "",
        type: "date",
        parentMinDate: "fromDate",
        minDate: "2020-01-01",
        maxDate: "2050-12-31",
      },
      {
        label: "Increase percentage per year",
        name: "increasePercentage",
        value: "increasePercentage",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-increase-pct",
        default: initialValues.increasePercentage || "",
        rows: null,
        type: "number",
        maxNumber: 100,
      },
    ],
    [
      initialValues.agreementdate,
      initialValues.amountEntry,
      initialValues.fromDate,
      initialValues.toDate,
      initialValues.increasePercentage,
      today,
    ]
  );

  const amountFieldsStudents = React.useMemo(
    () => [
      {
        label: "Number Of Students (approximately)",
        name: "numberOfStudents",
        value: "numberOfStudents",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-num-students",
        default: initialValues.numberOfStudents || "",
        rows: null,
        type: "number",
        minNumber: 1,
        maxNumber: 1000000,
      },
      {
  label: "Amount per student",
  name: "peramount",
  value: "peramount",
  md: 6,
  className: "w-95-per",
  required: true,
  id: "agreement-amount-per-student",
  default: initialValues.peramount || "",
  rows: null,
  type: "number",
  minNumber: 1,
  maxNumber: 1000000,
},
    ],
    [initialValues.numberOfStudents, initialValues.peramount]
  );

  const amountFieldsUpto = React.useMemo(
    () => [
      {
        label: "Up to students",
        name: "numberOfStudents",
        value: "numberOfStudents",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-upto-students",
        default: initialValues.numberOfStudents || "",
        rows: null,
        type: "number",
        minNumber: 1,
        maxNumber: 1000000,
      },
      {
        label: "Amount",
        name: "peramount",
        value: "peramount",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "agreement-amount",
        default: initialValues.peramount || "",
        rows: null,
        type: "number",
        minNumber: 1,
        maxNumber: 10000,
      },
      
    ],
    [initialValues.numberOfStudents, initialValues.peramount]
  );

  const subscriptionFieldDetails = React.useMemo(
    () => [
      {
        label: "Free SMS",
        name: "fsmscost",
        value: "fsmscost",
        md: 4,
        className: "w-95-per",
        required: true,
        id: "agreement-free-sms",
        default: initialValues.fsmscost || "",
        rows: null,
        type: "number",
        maxNumber: 100000,
      },
      {
        label: "Free Email",
        name: "femailcost",
        value: "femailcost",
        md: 4,
        className: "w-95-per",
        required: true,
        id: "agreement-free-email",
        default: initialValues.femailcost || "",
        rows: null,
        type: "number",
        maxNumber: 100000,
      },
      {
        label: "Whatsapp cost",
        name: "whatsappcost",
        value: "whatsappcost",
        md: 4,
        className: "w-95-per",
        required: false,
        id: "agreement-whatsapp-cost",
        default: initialValues.whatsappcost || "",
        rows: null,
        type: "number",
        maxNumber: 100,
      },
      {
        label: "Additional SMS cost",
        name: "smscost",
        value: "smscost",
        md: 4,
        className: "w-95-per",
        required: true,
        id: "agreement-sms-cost",
        default: initialValues.smscost || "",
        rows: null,
        type: "number",
        maxNumber: 100,
      },
      {
        label: "Additional Email cost",
        name: "emailcost",
        value: "emailcost",
        md: 4,
        className: "w-95-per",
        required: true,
        id: "agreement-email-cost",
        default: initialValues.emailcost || "",
        rows: null,
        type: "number",
        maxNumber: 100,
      },
      {
        label: "Additional IVR Service cost",
        name: "ivrcost",
        value: "ivrcost",
        md: 4,
        className: "w-95-per",
        required: false,
        id: "agreement-ivr-cost",
        default: initialValues.ivrcost || "",
        rows: null,
        type: "number",
        maxNumber: 100,
      },
    ],
    [
      initialValues.emailcost,
      initialValues.femailcost,
      initialValues.fsmscost,
      initialValues.gst,
      initialValues.increasePercentage,
      initialValues.ivrcost,
      initialValues.perId,
      initialValues.smscost,
      initialValues.whatsappcost,
    ]
  );

  const aggregatedDigitalTerms = React.useMemo(() => {
    const blocks = [];
    for (const fid of Object.keys(digitalFeatureSelection)) {
      const meta = DigitalMarketingFeatureList.find((f) => String(f.id) === String(fid));
      const name = meta?.name || `Feature #${fid}`;
      const terms = Array.isArray(meta?.terms_and_condition)
        ? meta.terms_and_condition.filter((t) => t != null && String(t).trim() !== "")
        : [];
      if (terms.length) blocks.push({ name, terms });
    }
    return blocks;
  }, [digitalFeatureSelection, DigitalMarketingFeatureList]);

  const erpCategoriesEntries = React.useMemo(() => {
    const grouped = (ERPFeatureList || []).reduce((acc, item) => {
      const cat = (item.category || "").trim() || "General";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [ERPFeatureList]);

  const erpCategoryRows = React.useMemo(() => {
    const rows = [];
    const size = 3;
    for (let i = 0; i < erpCategoriesEntries.length; i += size) {
      rows.push(erpCategoriesEntries.slice(i, i + size));
    }
    return rows;
  }, [erpCategoriesEntries]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        width: "100%",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#0a0a0a" }}>
            Agreements
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
            Create and edit agreements
          </Typography>
        </Box>

        <Box display="flex" gap={1} alignSelf={{ xs: "flex-start", sm: "center" }}>
          <Button
            variant="contained"
            startIcon={<VisibilityRounded />}
            onClick={() => history(-1)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 2.5,
              py: 1,
            }}
          >
            View agreements
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "#0a0a0a", fontWeight: 700, mb: 1 }}>
            Service plan 
          </Typography>
          <TextField
            select
            fullWidth
            label="Choose plan (package)"
            value={selectedPlanId}
            onChange={(e) => handlePlanChange(e.target.value)}
            size="small"
          >
            <MenuItem value="">— Select plan —</MenuItem>
            {servicePlanList.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name} — service: {plan.service_name || "—"}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "#0a0a0a", fontWeight: 700, mb: 1 }}>
            Services
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Checkbox checked={serviceToggles.erp} onChange={handleServiceToggleChange("erp")} />}
                label="ERP (modules)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Checkbox checked={serviceToggles.digital} onChange={handleServiceToggleChange("digital")} />}
                label="Other services (non-ERP)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Checkbox checked={serviceToggles.idCard} onChange={handleServiceToggleChange("idCard")} />}
                label="ID card"
              />
            </Grid>
          </Grid>
        </Paper>

        <SectionHeading>Institute details</SectionHeading>
        <DynamicForm
          key={`agreement-school-${dynamicFormKey}`}
          fieldDetails={schoolFieldDetails}
          updateParent={updateParent}
          loading={false}
        />

        <Box mt={3} mb={3}>
          <Divider />
        </Box>

        <SectionHeading>Contact person details</SectionHeading>
        <DynamicForm
          key={`agreement-contact-${dynamicFormKey}`}
          fieldDetails={contactFieldDetails}
          updateParent={updateParent}
          loading={false}
        />
        {!!phoneValidationError && (
          <Typography color="error" variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {phoneValidationError}
          </Typography>
        )}

        <Box mt={3} mb={3}>
          <Divider />
        </Box>

        <SectionHeading>Agreement basics</SectionHeading>
        <Grid container spacing={2} sx={{ mb: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              id="agreement-amount-entry"
              label="Up to student / Number of student"
              value={formData.amountEntry || ""}
              onChange={(e) => updateParent("amountEntry", e.target.value)}
              fullWidth
              required
              size="small"
              helperText="Choose the pricing basis for student count."
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: { maxHeight: 260, borderRadius: 2 },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                },
              }}
            >
              <MenuItem value="">
                <em>Select option</em>
              </MenuItem>
              <MenuItem value="Number of students">Number of students</MenuItem>
              <MenuItem value="Up to student">Up to student</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <DynamicForm
          key={`agreement-basics-${dynamicFormKey}`}
          fieldDetails={agreementBasicsFieldDetails}
          updateParent={updateParent}
          loading={false}
        />
        <Grid container spacing={2} sx={{ mt: 0.5, mb: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              id="agreement-gst-controlled"
              label="GST %"
              type="number"
              size="small"
              fullWidth
              required
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={formData.gst ?? ""}
              onChange={(e) => updateParent("gst", e.target.value)}
            />
          </Grid>
        </Grid>

        {formData.amountEntry === "Number of students" && (
          <DynamicForm
            key={`agreement-amount-students-${dynamicFormKey}`}
            fieldDetails={amountFieldsStudents}
            updateParent={updateParent}
            loading={false}
          />
        )}
        {formData.amountEntry === "Up to student" && (
          <DynamicForm
            key={`agreement-amount-upto-${dynamicFormKey}`}
            fieldDetails={amountFieldsUpto}
            updateParent={updateParent}
            loading={false}
          />
        )}

        <SectionHeading>Payment terms</SectionHeading>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: "divider" }}>
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1.5 }}>
            Payment terms apply to <b>ERP only</b> (incl. GST). Amount / percentage uses the ERP total.
          </Typography>
          <TextField
            select
            label="Edit payment terms by"
            value={paymentEditMode}
            onChange={(e) => setPaymentEditMode(e.target.value)}
            size="small"
            sx={{ minWidth: 260, mb: 2, mr: 2 }}
          >
            <MenuItem value="percentage">Percentage (%)</MenuItem>
            <MenuItem value="amount">Amount (₹)</MenuItem>
          </TextField>
          <TextField
            select
            id="termSelect"
            label="Number of payment terms"
            value={selectedTerm}
            onChange={handleTermChange}
            size="small"
            sx={{ minWidth: 260, mb: 2 }}
            required
          >
            <MenuItem value="">— Select —</MenuItem>
            <MenuItem value="1">1 term</MenuItem>
            <MenuItem value="2">2 terms</MenuItem>
            <MenuItem value="3">3 terms</MenuItem>
            <MenuItem value="4">4 terms</MenuItem>
          </TextField>

          {(formData.paymentTerms || []).map((term, index) => {
            const pct = Number(term.payment) || 0;
            const termAmount = (pct / 100) * paymentScheduleGrandTotal;
            const othersPct = (formData.paymentTerms || []).reduce(
              (sum, t, i) => (i === index ? sum : sum + (parseFloat(t?.payment) || 0)),
              0
            );
            const maxPctForRow = Math.max(0, 100 - othersPct);
            const maxTermAmount =
              paymentScheduleGrandTotal > 0 ? (paymentScheduleGrandTotal * maxPctForRow) / 100 : 0;
            const mode = paymentEditMode || "percentage";
            const termAmountInput =
              mode === "amount"
                ? String(term?.amount ?? "")
                : Number.isFinite(termAmount)
                  ? termAmount.toFixed(2)
                  : "0.00";
            return (
              <Box
                key={index}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#0a0a0a" }}>
                  Term {index + 1}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-start" }}>
                  {mode === "percentage" ? (
                    <TextField
                      type="number"
                      label="Payment %"
                      value={term.payment}
                      onChange={(e) => {
                        setPaymentEditMode("percentage");
                        handlePaymentChange(index, "payment", e.target.value);
                      }}
                      size="small"
                      sx={{ width: 160 }}
                      inputProps={{ min: 0, max: 100, step: "0.01" }}
                    />
                  ) : null}
                  <TextField
                    type="date"
                    label="Due date"
                    min="2020-01-01"
                    max="2050-12-31"
                    value={term.date}
                    onChange={(e) => handlePaymentChange(index, "date", e.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  {mode === "amount" ? (
                    <TextField
                      label="Amount (₹)"
                      type="number"
                      value={termAmountInput}
                      onChange={(e) => {
                        setPaymentEditMode("amount");
                        handlePaymentChange(index, "amount", e.target.value);
                      }}
                      size="small"
                      sx={{ minWidth: 190, maxWidth: 240 }}
                      inputProps={{
                        min: 0,
                        max: paymentScheduleGrandTotal > 0 ? Number(paymentScheduleGrandTotal.toFixed(2)) : undefined,
                        step: "0.01",
                      }}
                      helperText={
                        paymentScheduleGrandTotal > 0
                          ? `Max ₹ ${paymentScheduleGrandTotal.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })} (others auto-adjust)`
                          : "Enter agreement amounts first (total is 0)"
                      }
                    />
                  ) : (
                    <TextField
                      label="Amount (₹)"
                      value={Number.isFinite(termAmount) ? termAmount.toFixed(2) : "0.00"}
                      size="small"
                      sx={{ minWidth: 190, maxWidth: 240 }}
                      disabled
                      helperText="Auto from percentage"
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        <Box mt={3} mb={3}>
          <Divider />
        </Box>

        <SectionHeading>Subscription and usage</SectionHeading>
        <DynamicForm
          key={`agreement-subscription-${dynamicFormKey}`}
          fieldDetails={subscriptionFieldDetails}
          updateParent={updateParent}
          loading={false}
        />

        {serviceToggles.idCard && (
          <>
            <Box mt={3} mb={3}>
              <Divider />
            </Box>
            <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Settings color="red" sx={{ mr: 0.5 }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0a0a0a" }}>
                ID Card
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Number of students"
                  name="numberOfStudents"
                  id="agreement-idcard-students"
                  type="number"
                  value={formData.numberOfStudents || ""}
                  onChange={(e) => updateParent("numberOfStudents", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1, max: 1000000 }}
                  helperText={
                    serviceToggles.erp && formData.numberOfStudents
                      ? "Using ERP student count. You can edit it here for ID cards."
                      : "Enter student count for ID card pricing."
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Amount per Id card"
                  name="perId"
                  id="agreement-per-id"
                  type="number"
                  value={formData.perId || ""}
                  onChange={(e) => updateParent("perId", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, max: 1000 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="ID card total (₹)"
                  value={(
                    (parseFloat(formData.perId) || 0) * (parseFloat(formData.numberOfStudents) || 0)
                  ).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  fullWidth
                  size="small"
                  disabled
                  helperText="Auto: amount per card × students"
                />
              </Grid>
            </Grid>
          </>
        )}

        <Box mt={2} sx={{ display: "flex", flexDirection: "column" }}>
          {serviceToggles.erp && (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              borderColor: "divider",
              bgcolor: "background.paper",
              order: 1,
            }}
          >
            <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Settings color="primary" sx={{ mr: 0.5 }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0a0a0a" }}>
                ERP 
              </Typography>
            </Box>

            {erpCategoryRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No ERP features loaded. Check permissions or try refreshing.
              </Typography>
            ) : (
              erpCategoryRows.map((row, rowIdx) => (
                <Grid container spacing={2} key={`erp-cat-row-${rowIdx}`} sx={{ mb: 2 }}>
                  {row.map(([category, items]) => (
                    <Grid item xs={12} md={4} key={category}>
                      {(() => {
                        const categoryIds = items.map((f) => String(f.id));
                        const selectedInCategory = categoryIds.filter((id) => selectedErpFeatures.includes(id)).length;
                        const allSelected = categoryIds.length > 0 && selectedInCategory === categoryIds.length;
                        const partiallySelected = selectedInCategory > 0 && !allSelected;
                        return (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          height: "100%",
                          minHeight: 200,
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 2,
                          borderColor: "divider",
                          bgcolor: "background.paper",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                            pb: 0.75,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: "text.primary",
                            }}
                          >
                            {category}
                          </Typography>
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={partiallySelected}
                                onChange={(e) => {
                                  setSelectedErpFeatures((prev) => {
                                    const prevSet = new Set(prev);
                                    if (e.target.checked) {
                                      categoryIds.forEach((id) => prevSet.add(id));
                                    } else {
                                      categoryIds.forEach((id) => prevSet.delete(id));
                                    }
                                    return Array.from(prevSet);
                                  });
                                }}
                              />
                            }
                            label={<Typography variant="caption">All</Typography>}
                          />
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            maxHeight: 320,
                            pr: 0.5,
                          }}
                        >
                          {items.map((feature) => (
                            <FormControlLabel
                              key={feature.id}
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                ml: 0,
                                mb: 0.5,
                                "& .MuiFormControlLabel-label": { mt: 0.25 },
                              }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedErpFeatures.includes(String(feature.id))}
                                  onChange={(e) => {
                                    const idStr = String(feature.id);
                                    if (e.target.checked) {
                                      setSelectedErpFeatures((prev) =>
                                        prev.includes(idStr) ? prev : [...prev, idStr]
                                      );
                                    } else {
                                      setSelectedErpFeatures((prev) => prev.filter((x) => x !== idStr));
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                                  {feature.name}
                                </Typography>
                              }
                            />
                          ))}
                        </Box>
                      </Paper>
                        );
                      })()}
                    </Grid>
                  ))}
                  {Array.from({ length: Math.max(0, 3 - row.length) }).map((_, padIdx) => (
                    <Grid item xs={12} md={4} key={`erp-pad-${rowIdx}-${padIdx}`} sx={{ display: { xs: "none", md: "block" } }} />
                  ))}
                </Grid>
              ))
            )}
          </Paper>
          )}
{/* 4. Other Services */}
{/* Other Services */}
{serviceToggles.digital && (
<Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              borderColor: "divider",
              bgcolor: "background.paper",
              order: 2,
            }}
          >
            <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Settings color="secondary" sx={{ mr: 0.5 }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0a0a0a" }}>
                Other services
              </Typography>
            </Box>

  <Grid container spacing={2}>
    {DigitalMarketingFeatureList.map((feature) => {
      const idStr = String(feature.id);
      const checked = Object.prototype.hasOwnProperty.call(digitalFeatureSelection, idStr);
      const row = digitalFeatureSelection[idStr] || { price: "", renewal_price: "", billing_from: "", billing_to: "", calculation_type: feature.calculation_type || "monthwise" };
      const calculationType = row.calculation_type || feature.calculation_type || "monthwise";
      const calculationLabel = {
        yearwise: "Year Wise",
        monthwise: "Month Wise",
        studentcount: "Student Count",
      }[calculationType] || "Month Wise";
      const catalogUnit = (parseFloat(feature.price || 0) || 0);
      const unitAmount = isEditMode
        ? catalogUnit
        : (parseFloat(row.price || feature.price || 0) || 0);
      const rowMonthsCreate = !isEditMode ? getDigitalRowMonthCount(row.billing_from, row.billing_to) : null;
      const rowYearsCreate =
        !isEditMode && calculationType === "yearwise"
          ? getDigitalRowYearCount(row.billing_from, row.billing_to)
          : null;
      const quantityCreate = !isEditMode ? getDigitalFeatureQuantity(calculationType, row, formData) : null;
      const savedLineTotal = parseFloat(row.price || 0) || 0;
      const totalDisplay = isEditMode
        ? savedLineTotal.toFixed(2)
        : (unitAmount * (quantityCreate != null ? quantityCreate : 0)).toFixed(2);
      const resolvedQtyForExplain =
        calculationType === "studentcount"
          ? String(formData?.numberOfStudents ?? "").trim() === ""
            ? null
            : Number(formData.numberOfStudents) || 0
          : calculationType === "yearwise"
            ? rowYearsCreate
            : rowMonthsCreate;
      const calculationHelperText =
        isEditMode
          ? "saved agreement line"
          : otherServiceCalculationExplain(unitAmount, calculationType, resolvedQtyForExplain);
      return (
        <Grid item xs={12} key={feature.id}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDigitalFeatureSelection((prev) => ({
                            ...prev,
                            [idStr]: {
                              price: feature.price != null ? String(feature.price) : "",
                              renewal_price:
                                feature.renewal_price != null ? String(feature.renewal_price) : "",
                              calculation_type: feature.calculation_type || "monthwise",
                              billing_from: "",
                              billing_to: "",
                            },
                          }));
                        } else {
                          setDigitalFeatureSelection((prev) => {
                            const next = { ...prev };
                            delete next[idStr];
                            return next;
                          });
                        }
                      }}
                    />
                  }
                  label={<Typography variant="body2">{feature.name}</Typography>}
                />
              </Grid>
              {checked && (
                <>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label={isEditMode ? "Catalog price" : `${calculationLabel} Price`}
                      size="small"
                      fullWidth
                      value={unitAmount.toFixed(2)}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  {!isEditMode ? (
                    <>
                      <Grid item xs={6} md={3}>
                        <TextField
                          label="Billing from"
                          type="date"
                          size="small"
                          fullWidth
                          value={row.billing_from || ""}
                          onChange={(e) =>
                            setDigitalFeatureSelection((prev) => ({
                              ...prev,
                              [idStr]: { ...prev[idStr], billing_from: e.target.value },
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                          helperText={
                            calculationType === "monthwise" && rowMonthsCreate != null
                              ? `No. of months: ${rowMonthsCreate}`
                              : calculationType === "yearwise" && rowYearsCreate != null
                                ? `No. of years: ${rowYearsCreate}`
                                : calculationType === "studentcount"
                                  ? "Uses number of students from the agreement"
                                  : calculationLabel
                          }
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          label="Billing to"
                          type="date"
                          size="small"
                          fullWidth
                          value={row.billing_to || ""}
                          onChange={(e) =>
                            setDigitalFeatureSelection((prev) => ({
                              ...prev,
                              [idStr]: { ...prev[idStr], billing_to: e.target.value },
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">
                        Agreed amount is shown below (edit uses saved totals).
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Total Price"
                      size="small"
                      fullWidth
                      value={totalDisplay}
                      InputProps={{ readOnly: true }}
                      helperText={calculationHelperText}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
      );
    })}
  </Grid>
</Paper>
)}

          {/* <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Services
          </Typography>
          <Service selectedServices={selectedServices} setSelectedServices={setSelectedServices} /> */}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              Additional Pricing
            </Typography>
            <Button type="button" size="small" variant="outlined" onClick={addPriceArea}>+</Button>
            <Button type="button" size="small" variant="outlined" onClick={deletePriceArea}>-</Button>
          </Box>
          {priceAreas.map((id, index) => (
            <TextField
              key={id}
              multiline
              minRows={2}
              fullWidth
              sx={{ mb: 1.25 }}
              value={formData.pricing[index] || ""}
              onChange={(e) => {
                const updatedPricing = [...(formData.pricing || [])];
                updatedPricing[index] = e.target.value;
                setFormData((prev) => ({ ...prev, pricing: updatedPricing }));
              }}
            />
          ))}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              Additional Payment Terms
            </Typography>
            <Button type="button" size="small" variant="outlined" onClick={addTextArea}>+</Button>
            <Button type="button" size="small" variant="outlined" onClick={deleteTextArea}>-</Button>
          </Box>
          {textAreas.map((id, index) => (
            <TextField
              key={id}
              multiline
              minRows={2}
              fullWidth
              sx={{ mb: 1.25 }}
              value={formData.subscription[index] || ""}
              onChange={(e) => {
                const updatedSubscriptions = [...(formData.subscription || [])];
                updatedSubscriptions[index] = e.target.value;
                setFormData((prev) => ({ ...prev, subscription: updatedSubscriptions }));
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            Additional Plan Type
          </Typography>
          <Button type="button" size="small" variant="outlined" onClick={addAddonArea}>+</Button>
          <Button type="button" size="small" variant="outlined" onClick={deleteAddonArea}>-</Button>
        </Box>
        {addonAreas.map((id, index) => (
          <Box key={id} sx={{ display: "flex", flexDirection: "row", mb: 1.25, width: "100%", gap: 1.25 }}>
            <TextField
              multiline
              minRows={2}
              sx={{ width: "50%" }}
              value={(formData.addOns[index] || "").split(" || ")[0] || ""}
              onChange={(e) => {
                const updatedAddons = [...(formData.addOns || [])];
                const secondInput = (updatedAddons[index] || "").split(" || ")[1] || "";
                updatedAddons[index] = `${e.target.value} || ${secondInput}`;
                setFormData((prev) => ({ ...prev, addOns: updatedAddons }));
              }}
            />
            <TextField
              multiline
              minRows={2}
              sx={{ width: "50%" }}
              value={(formData.addOns[index] || "").split(" || ")[1] || ""}
              onChange={(e) => {
                const updatedAddons = [...(formData.addOns || [])];
                const firstInput = (updatedAddons[index] || "").split(" || ")[0] || "";
                updatedAddons[index] = `${firstInput} || ${e.target.value}`;
                setFormData((prev) => ({ ...prev, addOns: updatedAddons }));
              }}
            />
          </Box>
        ))}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            Plan Notes
          </Typography>
          <Button type="button" size="small" variant="outlined" onClick={addPlanNotesArea}>+</Button>
          <Button type="button" size="small" variant="outlined" onClick={deletePlanNotesArea}>-</Button>
        </Box>
        {planNotesAreas.map((id, index) => (
          <TextField
            key={`planNotes-${id}`}
            multiline
            minRows={2}
            fullWidth
            sx={{ mb: 1.25 }}
            value={formData.planNotes?.[index] || ""}
            onChange={(e) => {
              const updatedNotes = [...(formData.planNotes || [])];
              updatedNotes[index] = e.target.value;
              setFormData((prev) => ({ ...prev, planNotes: updatedNotes }));
            }}
            placeholder="Note details..."
          />
        ))}

        <Box mt={3} mb={3}>
          <Divider />
        </Box>

        <SectionHeading>Salesperson</SectionHeading>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: "divider",
                bgcolor: "#fafafa",
              }}
            >
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Sales person"
                value={formData.sales_person != null && formData.sales_person !== "" ? String(formData.sales_person) : ""}
                onChange={(e) => updateParent("sales_person", e.target.value)}
                SelectProps={{
                  displayEmpty: true,
                  MenuProps: {
                    PaperProps: {
                      sx: { maxHeight: 320, borderRadius: 2 },
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                    borderRadius: 2,
                  },
                }}
                helperText={salesPeopleList.length ? "Choose who owns this agreement" : "No sales users loaded — use Add salesperson"}
              >
                <MenuItem value="">
                  <em>Select salesperson</em>
                </MenuItem>
                {salesPeopleList.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)} sx={{ py: 1 }}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              type="button"
              variant="outlined"
              startIcon={<PersonAddAlt />}
              onClick={() => setSalespersonDialogOpen(true)}
              sx={{ textTransform: "none", mt: { xs: 0, md: 1 }, color: "#0a0a0a", borderColor: "grey.400" }}
            >
              Add salesperson
            </Button>
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.75 }}>
              Creates a user in the sales group and refreshes this list.
            </Typography>
          </Grid>
          {isEditMode && (
            <Grid item xs={12} md={6}>
              <Dropdown
                label="Company"
                name="company"
                value={formData.company || ""}
                onChange={(e) => updateParent("company", e.target.value ? e.target.value : null)}
                data={[{ id: "", name: "None" }, ...companiesList.map(c => ({ id: c.id, name: c.name || c.company_name || `Company ${c.id}` }))]}
                required={false}
                hideSelect={true}
                className="w-95-per"
              />
            </Grid>
          )}
        </Grid>

        {/* Document Management Section - Only in Edit Mode */}
        {isEditMode && (
          <>
            <Box mt={3} mb={3}>
              <Divider />
            </Box>
            <SectionHeading>Agreement documents</SectionHeading>
            
            {/* Existing Documents */}
            {existingDocuments.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Existing Documents
                </Typography>
                {existingDocuments.map((doc, index) => {
                  const formatDate = (dateString) => {
                    if (!dateString) return '';
                    try {
                      const date = new Date(dateString);
                      return date.toLocaleDateString('en-IN', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    } catch {
                      return dateString;
                    }
                  };
                  
                  const docIdForDelete = doc.agreement_document_id || doc.id;
                  const isMarkedForDelete = documentsToDelete.includes(docIdForDelete);
                  
                  return (
                    <Box
                      key={doc.id || index}
                      mb={1}
                      p={1.5}
                      border={1}
                      borderColor={isMarkedForDelete ? "error.main" : "grey.300"}
                      borderRadius={1}
                      bgcolor={isMarkedForDelete ? "error.light" : "background.paper"}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box flex={1}>
                        <Typography variant="body2" sx={{ textDecoration: isMarkedForDelete ? 'line-through' : 'none' }}>
                          {doc.file_name || `Document ${index + 1}`}
                        </Typography>
                        {doc.created && (
                          <Typography variant="caption" color="textSecondary">
                            Uploaded: {formatDate(doc.created)}
                          </Typography>
                        )}
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isMarkedForDelete}
                            onChange={() => handleToggleDeleteDocument(doc)}
                            color="error"
                          />
                        }
                        label="Delete"
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
            
            {/* New Documents to Upload */}
            <Box mb={2}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                New Documents to Upload
              </Typography>
              {newDocumentsToUpload.length > 0 && (
                <Box mb={1}>
                  {newDocumentsToUpload.map((file, index) => (
                    <Box
                      key={index}
                      mb={1}
                      p={1}
                      border={1}
                      borderColor="primary.main"
                      borderRadius={1}
                      bgcolor="action.hover"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2">{file.name}</Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveNewDocument(index)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<UploadFile />}
                onClick={() => documentFileInputRef.current?.click()}
                size="small"
              >
                Add Document
              </Button>
              <input
                ref={documentFileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={handleDocumentFileSelect}
                accept="application/pdf,image/*"
                multiple
              />
            </Box>
          </>
        )}

        {aggregatedDigitalTerms.length > 0 && (
          <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3, mt: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ color: "#0a0a0a" }}>
              Terms and conditions (selected other services)
            </Typography>
            {aggregatedDigitalTerms.map((block) => (
              <Box key={block.name} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {block.name}
                </Typography>
                <Box component="ol" sx={{ pl: 2.5, mt: 1, m: 0 }}>
                  {block.terms.map((t, i) => (
                    <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                      {t}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => history(mode === "quotation" ? "/quotation/add" : "/agreement/list")}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          {mode === "quotation" ? (
            <Button
              type="button"
              variant="contained"
              color="primary"
              disabled={!!error || submitDisable}
              onClick={handleGenerateQuotation}
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              Print Quotation
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="submit"
              disabled={!!error || submitDisable}
              onClick={handleSubmit}
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              Submit
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog
        open={salespersonDialogOpen}
        onClose={() => !salespersonSaving && setSalespersonDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#0a0a0a", fontWeight: 700 }}>Add salesperson</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a staff user in the sales group (same list as the dropdown). They can sign in with this username and password.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Username / display name"
                fullWidth
                required
                value={salespersonForm.username}
                onChange={(e) => setSalespersonForm((p) => ({ ...p, username: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mobile number"
                fullWidth
                required
                value={salespersonForm.mobile_num}
                onChange={(e) =>
                  setSalespersonForm((p) => ({
                    ...p,
                    mobile_num: String(e.target.value || "").replace(/\D/g, "").slice(0, 10),
                  }))
                }
                inputProps={{ inputMode: "numeric", maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email (optional)"
                fullWidth
                type="email"
                value={salespersonForm.email}
                onChange={(e) => setSalespersonForm((p) => ({ ...p, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Password"
                fullWidth
                required
                type="password"
                autoComplete="new-password"
                value={salespersonForm.password}
                onChange={(e) => setSalespersonForm((p) => ({ ...p, password: e.target.value }))}
                helperText="Minimum 6 characters."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => !salespersonSaving && setSalespersonDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" disabled={salespersonSaving} onClick={handleSaveNewSalesperson}>
            {salespersonSaving ? "Saving…" : "Create and select"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!submitDisable) setConfirmOpen(false);
        }}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 1120,
            height: { xs: "92vh", md: "88vh" },
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, color: "#0a0a0a", fontWeight: 700 }}>
          {isEditMode ? "Confirm agreement update" : "Confirm agreement create"}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            Please review the full summary before confirming.
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2,
            "& .MuiTypography-body2": { fontSize: "0.95rem" },
            "& .MuiTypography-caption": { fontSize: "0.82rem" },
            "& .MuiTypography-subtitle2": { fontSize: "1rem" },
            "& .MuiTableCell-root": { fontSize: "0.92rem", py: 1 },
            "& .MuiTableCell-head": { fontWeight: 700 },
          }}
        >
          <AgreementSummary data={getSummaryData()} requestProps={props} />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={submitDisable}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={submitDisable}
            onClick={() => {
              setConfirmOpen(false);
              if (pendingSubmitData) performSubmit(pendingSubmitData);
            }}
          >
            {submitDisable ? "Saving..." : (isEditMode ? "Confirm Update" : "Confirm Create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgreementAdd;
